import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Users, Shield, ShieldCheck, Settings2, ChevronDown, Menu, X, Construction, Network } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import UserManagement from "@/components/admin/UserManagement";
import DepartmentManagement from "@/components/admin/DepartmentManagement";
import IpWhitelist from "@/components/admin/IpWhitelist";
import PiiMasking from "@/components/admin/PiiMasking";
import CustomUserFields from "@/components/admin/CustomUserFields";

const governanceItems = [
  { id: "user-management", label: "User Management", icon: Users },
  { id: "departments", label: "Departments", icon: Network },
  { id: "ip-whitelist", label: "IP Whitelist", icon: Shield },
];

const dataControlItems = [
  { id: "pii-masking", label: "Schema/PII Masking", icon: ShieldCheck },
  { id: "custom-fields", label: "User Custom Fields", icon: Settings2 },
];

const allItems = [...governanceItems, ...dataControlItems];

const DataGovernance = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [active, setActive] = useState(() => {
    const fromUrl = searchParams.get("tab");
    return allItems.find((i) => i.id === fromUrl) ? fromUrl! : governanceItems[0].id;
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dcOpen, setDcOpen] = useState(true);
  const activeItem = allItems.find((i) => i.id === active);
  const isDataControlActive = dataControlItems.some((i) => i.id === active);

  const selectTab = (id: string) => {
    setActive(id);
    setSidebarOpen(false);
    setSearchParams((prev) => { prev.set("tab", id); return prev; });
  };

  return (
    <div className="flex flex-1 min-h-0 relative overflow-hidden">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-black/30 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-40 transition-transform duration-300",
          "md:relative md:inset-auto md:z-auto md:translate-x-0 md:flex md:shrink-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <aside className="flex h-full w-56 flex-col border-r border-border bg-sidebar">
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Data Governance</h2>
            <Button variant="ghost" size="icon" className="h-6 w-6 md:hidden" onClick={() => setSidebarOpen(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <nav className="flex-1 space-y-1 px-2 pb-4 overflow-y-auto">
            {governanceItems.map((item) => (
              <button
                key={item.id}
                onClick={() => selectTab(item.id)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  active === item.id
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground hover:bg-sidebar-accent/50",
                )}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </button>
            ))}

            <Collapsible open={dcOpen} onOpenChange={setDcOpen}>
              <CollapsibleTrigger asChild>
                <button
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    isDataControlActive
                      ? "text-sidebar-accent-foreground"
                      : "text-sidebar-foreground hover:bg-sidebar-accent/50",
                  )}
                >
                  <Shield className="h-4 w-4" />
                  Data Control
                  <ChevronDown className={cn("ml-auto h-3 w-3 transition-transform", dcOpen && "rotate-180")} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="ml-3 space-y-0.5 border-l border-border pl-2">
                  {dataControlItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        selectTab(item.id);
                      }}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors",
                        active === item.id
                          ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                          : "text-sidebar-foreground hover:bg-sidebar-accent/50",
                      )}
                    >
                      <item.icon className="h-3.5 w-3.5" />
                      {item.label}
                    </button>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </nav>
        </aside>
      </div>

      {/* Main content */}
      <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
        {/* Mobile toolbar */}
        <div className="flex items-center gap-2 border-b border-border px-3 py-2 md:hidden shrink-0">
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setSidebarOpen(true)}>
            <Menu className="h-5 w-5" />
          </Button>
          <span className="text-sm font-medium">{activeItem?.label}</span>
        </div>

        {active === "user-management" ? (
          <UserManagement />
        ) : active === "departments" ? (
          <DepartmentManagement />
        ) : active === "ip-whitelist" ? (
          <IpWhitelist />
        ) : active === "pii-masking" ? (
          <PiiMasking />
        ) : active === "custom-fields" ? (
          <CustomUserFields />
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-accent">
              <Construction className="h-8 w-8 text-accent-foreground" />
            </div>
            <h1 className="text-2xl font-semibold">{activeItem?.label}</h1>
            <p className="max-w-md text-center text-muted-foreground">
              This feature is coming soon. Check back later for updates.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataGovernance;
